import * as Select from '@radix-ui/react-select';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ElementRef, type PropsWithChildren, forwardRef } from 'react';
import { type InputBaseProps } from '../base';
import { DropdownTrigger } from './DropdownTrigger';

const dropdown = cva(
  'flex flex-col bg-surface-strong rounded-s p-xs shadow-l z-50',
);

export type DropdownProps = PropsWithChildren<VariantProps<typeof dropdown>> &
  InputBaseProps & {
    id?: string;
    name?: string;
    defaultValue?: string;
    placeholder?: string;
    ariaLabel?: string;
    disabled?: boolean;
    required?: boolean;
    value?: string;
    onChange: (value: string) => void;
    variant?: 'default' | 'chip';
  };

/**
 * Controlled dropdown component. Supports items, labels and groups of items.
 *
 *
 *
 * @components
 * Content ==> The component that pops out when the select is open.
 * Viewport ==> The scrollable element that contains the dropdown items.
 *
 * @example
 * <Dropdown
 *   name="realHousewives"
 *   defaultValue="Lisa Vanderpump"
 *   placeholder="Select a real housewife..."
 * >
 *   <DropdownGroup label="Real Housewives of Beverly Hills tier list">
 *     {realHousewives.map((housewife) => (
 *       <DropdownItem
 *         key={housewife.id}
 *         value={housewife.name}
 *         text={housewife.name}
 *       />
 *     )}
 *   </DropdownGroup>
 * </Dropdown>
 */
// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it. (Dropdown's ref goes to DropdownTrigger, not a DOM node -- make sure DropdownTrigger actually applies it to Select.Trigger.)
const Dropdown = forwardRef<ElementRef<'button'>, DropdownProps>(
  (
    {
      ariaLabel,
      children,
      disabled,
      onChange,
      intent,
      placeholder,
      required,
      value,
      variant,
      ...props
    },
    forwardedRef,
  ) => (
    <Select.Root
      name={props.name}
      disabled={disabled}
      required={required}
      onValueChange={(result) => {
        onChange(result);
      }}
      value={value}
    >
      <DropdownTrigger
        {...props}
        placeholder={placeholder}
        ref={forwardedRef}
        intent={intent}
        ariaLabel={ariaLabel}
        variant={variant}
      />
      <Select.Portal>
        <Select.Content className={dropdown()}>
          <Select.Viewport>{children}</Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  ),
);

Dropdown.displayName = 'Dropdown';

export { Dropdown };
